import React from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from 'context/AuthContext';

const ProgressSummary = ({
  overallProgress = 35,
  totalQuizScore = 82,
  completedChapters = 1,
  totalChapters = 4,
  className = ""
}) => {

  const { courseOverview } = useAuth();

  // const getStreakMessage = () => {
  //   if (learningStreak === 0) return "Start your learning streak today!";
  //   if (learningStreak === 1) return "Great start! Keep it up!";
  //   if (learningStreak < 7) return "Building momentum!";
  //   return "Amazing consistency!";
  // };

  const getProgressLevel = () => {
    if (courseOverview?.completedCourses < 25) return { level: "Beginner", color: "secondary" };
    if (courseOverview?.completedCourses < 50) return { level: "Learning", color: "accent" };
    if (courseOverview?.completedCourses < 75) return { level: "Progressing", color: "warning" };
    return { level: "Advanced", color: "success" };
  };

  const progressLevel = getProgressLevel();

  const totalSlidesofChapters = courseOverview?.coursesProgress?.reduce((acc, course) => {
    return acc + (course?.progress || 0);
  }, 0) || 0;

  const progressPercent = (totalSlidesofChapters / 400 * 100).toFixed(0);
  const circleCircumference = 2 * Math.PI * 50;
  const strokeLength = (progressPercent / 100) * circleCircumference;

  return (
    <div className={`bg-surface rounded-xl border border-border card-shadow ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold font-heading text-text-primary">
              Your Learning Progress
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              Track your AI fundamentals journey
            </p>
          </div>

          <div className={`
            flex items-center space-x-2 px-3 py-2 rounded-lg
            bg-${progressLevel.color}-50
          `}>
            <Icon
              name="TrendingUp"
              size={16}
              color={`var(--color-${progressLevel.color})`}
              strokeWidth={2}
            />
            <span className={`text-sm font-medium text-${progressLevel.color}-700`}>
              {progressLevel.level}
            </span>
          </div>
        </div>

        {/* Main Progress Circle */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative w-32 h-32 mb-4">
            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r="50"
                stroke="var(--color-border-light)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="60"
                cy="60"
                r="50"
                stroke="var(--color-primary)"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${strokeLength} ${circleCircumference}`}
                strokeLinecap="round"
                className="progress-animation"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold font-heading text-text-primary">
                {progressPercent}%
              </span>
              <span className="text-xs text-text-secondary">Complete</span>
            </div>
          </div>

          {/* <p className="max-w-xs text-sm text-center text-text-secondary">
            {getStreakMessage()}
          </p> */}
        </div>

        {/* Progress Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 text-center rounded-lg bg-surface-secondary">
            <div className="flex items-center justify-center w-10 h-10 mx-auto mb-2 rounded-lg bg-success-50">
              <Icon name="BookCheck" size={20} color="var(--color-success)" strokeWidth={2} />
            </div>
            <div className="text-lg font-bold font-heading text-text-primary">
              {courseOverview?.completedCourses}/{totalChapters}
            </div>
            <div className="text-xs text-text-secondary">Chapters</div>
          </div>

          <div className="p-4 text-center rounded-lg bg-surface-secondary">
            <div className="flex items-center justify-center w-10 h-10 mx-auto mb-2 rounded-lg bg-accent-50">
              <Icon name="Target" size={20} color="var(--color-accent)" strokeWidth={2} />
            </div>
            <div className="text-lg font-bold font-heading text-text-primary">
              {Math.round(courseOverview?.quizAvg)}%
            </div>
            <div className="text-xs text-text-secondary">Avg Quiz Score</div>
          </div>
          {/* 
          <div className="p-4 text-center rounded-lg bg-surface-secondary">
            <div className="flex items-center justify-center w-10 h-10 mx-auto mb-2 rounded-lg bg-warning-50">
              <Icon name="Flame" size={20} color="var(--color-warning)" strokeWidth={2} />
            </div>
            <div className="text-lg font-bold font-heading text-text-primary">
              {learningStreak}
            </div>
            <div className="text-xs text-text-secondary">Day Streak</div>
          </div> */}

          {/* <div className="p-4 text-center rounded-lg bg-surface-secondary">
            <div className="flex items-center justify-center w-10 h-10 mx-auto mb-2 rounded-lg bg-secondary-50">
              <Icon name="GraduationCap" size={20} color="var(--color-secondary)" strokeWidth={2} />
            </div>
            <div className="text-lg font-bold font-heading text-text-primary">
              {totalLessonsCompleted}/{totalLessons}
            </div>
            <div className="text-xs text-text-secondary">Lessons</div>
          </div> */}
        </div>

        {/* Achievement Badges */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold font-heading text-text-primary">
            Recent Achievements
          </h3>

          <div className="flex flex-wrap gap-2">
            {completedChapters > 0 && (
              <div className="flex items-center px-3 py-2 space-x-2 rounded-full bg-success-50">
                <Icon name="Award" size={14} color="var(--color-success)" strokeWidth={2} />
                <span className="text-xs font-medium text-success-700">First Chapter</span>
              </div>
            )}

            {/* {learningStreak >= 3 && (
              <div className="flex items-center px-3 py-2 space-x-2 rounded-full bg-warning-50">
                <Icon name="Zap" size={14} color="var(--color-warning)" strokeWidth={2} />
                <span className="text-xs font-medium text-warning-700">3-Day Streak</span>
              </div>
            )} */}

            {totalQuizScore >= 80 && (
              <div className="flex items-center px-3 py-2 space-x-2 rounded-full bg-accent-50">
                <Icon name="Star" size={14} color="var(--color-accent)" strokeWidth={2} />
                <span className="text-xs font-medium text-accent-700">High Scorer</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressSummary;